import { Button, Form, TextInput, useWindowLocation } from '@prishapolicy/shared/ui';
import { useState } from 'react';
import { object, string } from 'yup';

const phoneRegex = /^\d{10}$/;

const pospLeadValidation = object({
  personalDetails_fullName: string().trim().required('Full name is required'),
  personalDetails_email: string().trim().email('Enter a valid email').required('Email is required'),
  personalDetails_phone: string()
    .matches(phoneRegex, 'Enter a valid 10-digit phone number')
    .required('Phone number is required'),
});

type SubmissionStatus = 'idle' | 'pending' | 'success' | 'error';

export const PospLeadForm = () => {
  const currentUrl = useWindowLocation();
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>('idle');

  return (
    <section id="become-a-posp" className="scroll-mt-24 bg-blue-200 py-12 md:py-16 lg:py-20">
      <div className="container grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="pt-2 lg:pt-8">
          <p className="text-primary text-body-2 mb-3 font-bold uppercase tracking-[0.16em]">
            Become a PrishaPolicy POSP
          </p>
          <h2 className="text-title-5 mb-5 font-bold leading-tight md:text-[40px] lg:text-[48px]">
            Start your insurance career with the right support
          </h2>
          <p className="text-body-1 mb-8 max-w-[620px] leading-relaxed">
            Share a few details and our POSP team will guide you through eligibility, documents, training and
            certification.
          </p>
          <ul className="text-body-2 grid gap-4 md:grid-cols-2 lg:grid-cols-1">
            {[
              'Guidance on eligibility and documents',
              'Help through training and certification',
              'A callback from our dedicated POSP team',
            ].map((benefit) => (
              <li key={benefit} className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="bg-secondary mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-bold"
                >
                  ✓
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-large bg-white p-6 shadow-lg md:p-8 lg:p-10">
          <div className="mb-7">
            <h3 className="text-title-4 mb-2 font-bold">Request a callback</h3>
            <p className="text-grey-700">Our team will contact you to explain the next steps.</p>
          </div>

          <Form
            initialValues={{
              personalDetails_fullName: '',
              personalDetails_email: '',
              personalDetails_phone: '',
            }}
            validationSchema={pospLeadValidation}
            onSubmit={async (values, { resetForm }) => {
              setSubmissionStatus('pending');

              try {
                const response = await fetch('/api/posp-callback', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    fullName: values.personalDetails_fullName,
                    email: values.personalDetails_email,
                    phone: values.personalDetails_phone,
                    sourceUrl: currentUrl || undefined,
                  }),
                });

                if (!response.ok) throw new Error('Callback request failed');

                resetForm();
                setSubmissionStatus('success');
              } catch {
                setSubmissionStatus('error');
              }
            }}
          >
            <div className="grid gap-4">
              <TextInput
                name="personalDetails_fullName"
                label="Full name"
                placeholder="Enter your full name"
                autoComplete="name"
              />
              <TextInput
                name="personalDetails_email"
                label="Email address"
                placeholder="Enter your email address"
                type="email"
                autoComplete="email"
              />
              <TextInput
                name="personalDetails_phone"
                label="Phone number"
                placeholder="10-digit mobile number"
                inputMode="numeric"
                autoComplete="tel"
              />

              <Button
                type="submit"
                text={submissionStatus === 'pending' ? 'Submitting…' : 'Request a callback'}
                size="large"
                className="mt-2 w-full"
                disabled={submissionStatus === 'pending'}
              />

              <p className="text-grey-600 text-body-3">
                By submitting, you agree that PrishaPolicy may contact you about becoming a POSP.
              </p>

              <div aria-live="polite">
                {submissionStatus === 'success' ? (
                  <p className="bg-surface-green text-success rounded-small px-4 py-3 font-medium">
                    Thank you. Our POSP team will contact you soon.
                  </p>
                ) : null}
                {submissionStatus === 'error' ? (
                  <p className="bg-surface-red text-danger rounded-small px-4 py-3 font-medium">
                    We could not submit your details. Please try again.
                  </p>
                ) : null}
              </div>
            </div>
          </Form>
        </div>
      </div>
    </section>
  );
};
